import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { ResidentEndSheet } from './ResidentEndSheet.jsx';
import { localDateIso } from '../local-date.js';
import { catalogFits } from '../saltwater-tools.js';

// ─── Livestock section ───────────────────────────────────
// Catalog-backed search (debounced) + free-text fallback. Each row
// has inline qty controls, a quick-edit modal for source / added_at /
// notes, and a delete. Sensei narrative re-fetches automatically the
// next time the score recalculates because POST/DELETE livestock
// fire a recalculateScoreForTank.

const KIND_EMOJI = { fish: '🐟', coral: '🪸', invert: '🦐' };
const KIND_LABEL = { fish: 'Fish', coral: 'Coral', invert: 'Invert' };

export function LivestockSection({ tank, onReload }) {
  const [ending, setEnding] = useState(null);
  const residents = tank.livestock || [];
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [results, setResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);
  const [freeText, setFreeText] = useState(null);

  // Debounced catalog search. AbortController prevents a slow earlier
  // request from clobbering a faster newer one (race), and the unmount
  // cleanup avoids a stray fetch / setState after the sheet closes.
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);
  const runSearch = (term, kind) => {
    if (!term || term.trim().length < 2) {
      setResults([]);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setSearchBusy(true);
    api(
      `/api/livestock-catalog?q=${encodeURIComponent(term.trim())}${kind ? `&kind=${kind}` : ''}&limit=12`,
      { signal: ctl.signal }
    )
      .then((rows) => {
        if (ctl.signal.aborted) return;
        setResults(rows || []);
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        setResults([]);
      })
      .finally(() => {
        if (!ctl.signal.aborted) setSearchBusy(false);
      });
  };
  const onSearchChange = (term) => {
    setQ(term);
    setErr('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term, kindFilter), 220);
  };
  const onKindChange = (kind) => {
    setKindFilter(kind);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runSearch(q, kind);
  };

  const addFromCatalog = async (entry) => {
    setBusy(`add:${entry.id}`);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/livestock`, {
        method: 'POST',
        body: { catalog_id: entry.id, qty: 1, added_at: localDateIso() },
      });
      await onReload();
      setQ('');
      setResults([]);
    } catch (e) {
      setErr(e.message || 'Could not add livestock.');
    } finally {
      setBusy(null);
    }
  };

  const addFreeTextRow = async () => {
    if (!freeText) return;
    if (!freeText.common_name?.trim()) {
      setErr('Common name is required.');
      return;
    }
    if (!['fish', 'coral', 'invert'].includes(freeText.kind)) {
      setErr('Pick a kind.');
      return;
    }
    setBusy('add:free');
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/livestock`, {
        method: 'POST',
        body: {
          common_name: freeText.common_name.trim().slice(0, 100),
          scientific_name: freeText.scientific_name?.trim().slice(0, 120) || null,
          kind: freeText.kind,
          qty: Math.max(1, Number(freeText.qty) || 1),
          added_at: freeText.added_at || null,
        },
      });
      await onReload();
      setFreeText(null);
    } catch (e) {
      setErr(e.message || 'Could not add livestock.');
    } finally {
      setBusy(null);
    }
  };

  const updateQty = async (row, delta) => {
    const next = Math.max(1, row.qty + delta);
    if (next === row.qty) return;
    setBusy(`qty:${row.id}`);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/livestock/${row.id}`, {
        method: 'PATCH',
        body: { qty: next },
      });
      await onReload();
    } catch (e) {
      setErr(e.message || 'Could not update.');
    } finally {
      setBusy(null);
    }
  };

  const saveEdits = async () => {
    if (!editing) return;
    setBusy(`edit:${editing.id}`);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/livestock/${editing.id}`, {
        method: 'PATCH',
        body: {
          qty: Math.max(1, Number(editing.qty) || 1),
          added_at: editing.added_at || null,
          source: editing.source || null,
          notes: editing.notes || null,
        },
      });
      await onReload();
      setEditing(null);
    } catch (e) {
      setErr(e.message || 'Could not save.');
    } finally {
      setBusy(null);
    }
  };

  const grouped = useMemo(() => {
    const out = { fish: [], coral: [], invert: [] };
    for (const r of residents) {
      // Unknown kinds fall through to fish so we don't silently drop rows.
      const bucket = out[r.kind] || out.fish;
      bucket.push(r);
    }
    return out;
  }, [residents]);

  return (
    <section className="ts-section">
      <div className="ts-section-head">
        <h3 className="font-heading">Livestock</h3>
        <p className="ts-hint">
          What's living in here. Sensei uses this to name your residents in the narrative instead of
          saying "your fish."
        </p>
      </div>

      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}

      <div className="ls-search">
        <input
          type="search"
          value={q}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={
            catalogFits(tank) ? 'Search 285 saltwater species…' : 'Saltwater species list only…'
          }
          aria-label="Search livestock catalog"
        />
        <div className="ls-kinds" role="radiogroup" aria-label="Filter by kind">
          {(catalogFits(tank) ? ['', 'fish', 'coral', 'invert'] : ['', 'fish', 'invert']).map(
            (k) => (
              <button
                key={k || 'all'}
                type="button"
                className={`ts-chip${kindFilter === k ? ' ts-chip-active' : ''}`}
                role="radio"
                aria-checked={kindFilter === k}
                onClick={() => onKindChange(k)}
              >
                {k ? `${KIND_EMOJI[k]} ${KIND_LABEL[k]}` : 'All'}
              </button>
            )
          )}
        </div>
      </div>

      {(searchBusy || results.length > 0) && (
        <ul className="ls-results">
          {searchBusy && <li className="ls-empty">Searching…</li>}
          {!searchBusy &&
            results.map((r) => (
              <li key={r.id} className="ls-result">
                <span className="ls-emoji" aria-hidden="true">
                  {KIND_EMOJI[r.kind] || '·'}
                </span>
                <div className="ls-result-text">
                  <div className="ls-result-name">{r.common_name}</div>
                  <div className="ls-result-sci">
                    <em>{r.scientific_name}</em>
                    {r.beginner_score != null && ` · beginner ${r.beginner_score}/10`}
                    {r.care_level && ` · ${r.care_level}`}
                  </div>
                </div>
                <button
                  type="button"
                  className="ts-chip"
                  onClick={() => addFromCatalog(r)}
                  disabled={busy === `add:${r.id}`}
                >
                  {busy === `add:${r.id}` ? '…' : 'Add'}
                </button>
              </li>
            ))}
          {!searchBusy && q.trim().length >= 2 && results.length === 0 && (
            <li className="ls-empty">No matches. Use "Add custom" below.</li>
          )}
        </ul>
      )}

      {/* Free-text fallback */}
      {freeText ? (
        <div className="ts-add-form">
          <div className="ts-add-head">
            <h4 className="font-heading">Add custom species</h4>
            <button type="button" className="ts-chip" onClick={() => setFreeText(null)}>
              Cancel
            </button>
          </div>
          <div className="ts-row-body" style={{ marginTop: 10 }}>
            <label className="ts-field" style={{ minWidth: 160, flex: '1 1 160px' }}>
              <span>Common name</span>
              <input
                type="text"
                value={freeText.common_name}
                maxLength={100}
                onChange={(e) => setFreeText({ ...freeText, common_name: e.target.value })}
                placeholder="Banggai cardinal"
              />
            </label>
            <label className="ts-field" style={{ minWidth: 160, flex: '1 1 160px' }}>
              <span>Scientific (optional)</span>
              <input
                type="text"
                value={freeText.scientific_name}
                maxLength={120}
                onChange={(e) => setFreeText({ ...freeText, scientific_name: e.target.value })}
                placeholder="Pterapogon kauderni"
              />
            </label>
          </div>
          <div className="ts-row-body">
            <label className="ts-field" style={{ minWidth: 100 }}>
              <span>Kind</span>
              <select
                value={freeText.kind}
                onChange={(e) => setFreeText({ ...freeText, kind: e.target.value })}
              >
                <option value="">Pick…</option>
                <option value="fish">Fish</option>
                {catalogFits(tank) && <option value="coral">Coral</option>}
                <option value="invert">Invert</option>
              </select>
            </label>
            <label className="ts-field" style={{ width: 70 }}>
              <span>Qty</span>
              <input
                type="number"
                min="1"
                value={freeText.qty}
                onChange={(e) => setFreeText({ ...freeText, qty: e.target.value })}
              />
            </label>
            <label className="ts-field" style={{ minWidth: 140 }}>
              <span>Added</span>
              <input
                type="date"
                max={localDateIso()}
                value={freeText.added_at}
                onChange={(e) => setFreeText({ ...freeText, added_at: e.target.value })}
              />
            </label>
            <button
              type="button"
              className="ts-save"
              onClick={addFreeTextRow}
              disabled={busy === 'add:free'}
            >
              {busy === 'add:free' ? '…' : 'Add'}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="ts-add-btn"
          onClick={() =>
            setFreeText({
              common_name: '',
              scientific_name: '',
              kind: '',
              qty: 1,
              added_at: localDateIso(),
            })
          }
        >
          + Add custom species
        </button>
      )}

      {/* Resident list */}
      {residents.length === 0 ? (
        <p className="ts-hint" style={{ marginTop: 14 }}>
          Nothing logged yet. Search above or add a custom species.
        </p>
      ) : (
        <ul className="ls-list">
          {['fish', 'coral', 'invert'].map((kind) =>
            grouped[kind].length === 0 ? null : (
              <li key={kind} className="ls-group">
                <div className="ls-group-head">
                  <span aria-hidden="true">{KIND_EMOJI[kind]}</span>
                  <span>
                    {KIND_LABEL[kind]} ({grouped[kind].length})
                  </span>
                </div>
                <ul>
                  {grouped[kind].map((r) => (
                    <li key={r.id} className="ls-item">
                      <div className="ls-item-text">
                        <div className="ls-item-name">{r.common_name}</div>
                        {r.scientific_name && (
                          <div className="ls-item-sci">
                            <em>{r.scientific_name}</em>
                          </div>
                        )}
                      </div>
                      <div className="ls-qty">
                        <button
                          type="button"
                          className="ts-chip"
                          onClick={() => updateQty(r, -1)}
                          disabled={busy === `qty:${r.id}` || r.qty <= 1}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <span className="ls-qty-num">{r.qty}</span>
                        <button
                          type="button"
                          className="ts-chip"
                          onClick={() => updateQty(r, +1)}
                          disabled={busy === `qty:${r.id}`}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                      <div className="ls-item-actions">
                        <button
                          type="button"
                          className="ts-chip"
                          onClick={() =>
                            setEditing({
                              id: r.id,
                              qty: r.qty,
                              added_at: r.added_at ? String(r.added_at).slice(0, 10) : '',
                              source: r.source || '',
                              notes: r.notes || '',
                              common_name: r.common_name,
                            })
                          }
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="ts-chip ts-chip-danger"
                          onClick={() => setEnding(r)}
                        >
                          No longer in the tank
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            )
          )}
        </ul>
      )}

      {ending && (
        <ResidentEndSheet
          tank={tank}
          resident={ending}
          onClose={() => setEnding(null)}
          onDone={onReload}
        />
      )}

      {editing && (
        <div className="ts-overlay" onClick={() => setEditing(null)}>
          <div
            className="ts-sheet ls-edit-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <header className="ts-header">
              <div>
                <div
                  className="font-heading"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                  }}
                >
                  Edit livestock
                </div>
                <h2
                  className="font-heading"
                  style={{ fontSize: 20, fontWeight: 600, marginTop: 2 }}
                >
                  {editing.common_name.toUpperCase()}
                </h2>
              </div>
              <button
                type="button"
                aria-label="Close"
                className="ts-close"
                onClick={() => setEditing(null)}
              >
                ×
              </button>
            </header>

            <div className="ts-row-body">
              <label className="ts-field" style={{ width: 80 }}>
                <span>Qty</span>
                <input
                  type="number"
                  min="1"
                  value={editing.qty}
                  onChange={(e) => setEditing({ ...editing, qty: e.target.value })}
                />
              </label>
              <label className="ts-field" style={{ minWidth: 160 }}>
                <span>Added</span>
                <input
                  type="date"
                  max={localDateIso()}
                  value={editing.added_at}
                  onChange={(e) => setEditing({ ...editing, added_at: e.target.value })}
                />
              </label>
            </div>
            <div className="ts-row-body">
              <label className="ts-field" style={{ minWidth: 200, flex: '1 1 200px' }}>
                <span>Source</span>
                <input
                  type="text"
                  value={editing.source}
                  maxLength={80}
                  onChange={(e) => setEditing({ ...editing, source: e.target.value })}
                  placeholder="LFS · online · frag swap"
                />
              </label>
            </div>
            <div className="ts-row-body">
              <label className="ts-field" style={{ minWidth: 240, flex: '1 1 240px' }}>
                <span>Notes</span>
                <input
                  type="text"
                  value={editing.notes}
                  maxLength={500}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  placeholder="Quarantined 4 weeks · pairs with anemone"
                />
              </label>
              <button
                type="button"
                className="ts-save"
                onClick={saveEdits}
                disabled={busy === `edit:${editing.id}`}
              >
                {busy === `edit:${editing.id}` ? '…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
